import { useObservable } from '@lib/common';
import {
  employeeQuery,
  employeeService,
  GetInvoiceRunRequest,
  invoiceRunQuery,
  invoiceRunService,
  InvoiceStatusFilter,
  invoiceStatusQuery,
  invoiceStatusService,
} from 'app/state';
import { useCallback, useEffect } from 'react';

export function usePollInvoiceStatuses(filters?: InvoiceStatusFilter, continuePolling?: boolean) {
  const statuses = useObservable(invoiceStatusQuery.invoiceStatuses);
  const corporateAccounts = useObservable(invoiceStatusQuery.corporateAccounts);

  useEffect(() => {
    return invoiceStatusService.pollInvoiceStatuses({ ...filters }, continuePolling);
  }, [filters, continuePolling]);

  return {
    statuses,
    corporateAccounts,
  };
}

export function useGetInvoiceStatuses(filters?: InvoiceStatusFilter) {
  const statuses = useObservable(invoiceStatusQuery.invoiceStatuses);
  const corporateAccounts = useObservable(invoiceStatusQuery.corporateAccounts);
  const loading = useObservable(invoiceStatusQuery.loading);

  useEffect(() => {
    return invoiceStatusService.getInvoiceStatuses({ ...filters });
  }, [filters]);

  return {
    statuses,
    corporateAccounts,
    loading,
  };
}

export function usePollInvoiceRunById(id: string, continuePolling?: boolean) {
  const run = useObservable(invoiceRunQuery.invoiceRun);

  useEffect(() => {
    return invoiceRunService.getInvoiceRunById(id, continuePolling);
  }, [id, continuePolling]);

  return {
    run,
  };
}

export function useGetInvoiceRuns(request?: GetInvoiceRunRequest) {
  const runs = useObservable(invoiceRunQuery.invoiceRuns);
  const loading = useObservable(invoiceRunQuery.loading);

  useEffect(() => {
    invoiceRunService.getInvoiceRuns({ ...request }, {});
  }, [request]);

  const loadMore = useCallback(
    (continuationToken: string) => {
      invoiceRunService.getInvoiceRuns({ ...request }, { continuationToken });
    },
    [request]
  );

  const { continuationToken, isDone } = useObservable(invoiceRunQuery.pagination);

  return {
    runs,
    loading,
    loadMore,
    continuationToken,
    isDone,
  };
}

export function useGetEmployees(propertyId: string) {
  useEffect(() => {
    employeeService.getEmployees({ propertyId });
  }, [propertyId]);

  return {
    employees: useObservable(employeeQuery.employees),
  };
}
